"use client";

import { useComponentTranslations, type AboutTranslationKey } from "@/app/components/LanguageProvider";
import { motion } from "framer-motion";
import Image from "next/image";
import ValuesCarousel from "@/app/components/ValuesCarousel";
import PageHero from "@/app/components/PageHero";
import PageContainer from "@/app/components/PageContainer";
import PageSection from "@/app/components/PageSection";
import SectionTitle from "@/app/components/SectionTitle";
import GlassCard from "@/app/components/GlassCard";
import PageCta from "@/app/components/PageCta";
import { CONTACTS } from "@/lib/site-contacts";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";

const ADVANTAGES = [
  { title: "advantage1Title", desc: "advantage1Desc" },
  { title: "advantage2Title", desc: "advantage2Desc" },
  { title: "advantage3Title", desc: "advantage3Desc" },
  { title: "advantage4Title", desc: "advantage4Desc" },
] as { title: AboutTranslationKey; desc: AboutTranslationKey }[];

export default function AboutClient() {
  const { t: aboutT, translationsLoaded } = useComponentTranslations("About");

  const at = (key: AboutTranslationKey, fallback?: string) => {
    if (!translationsLoaded) return fallback ?? key;
    return aboutT(key, fallback) ?? fallback ?? key;
  };

  const locale = useLocale();
  const isRu = locale.startsWith("ru");

  if (!translationsLoaded) {
    return (
      <div className="min-h-screen text-white">
        <PageContainer>
          <PageHero
            title={at("aboutHeroTitle", "About Us")}
            description={at("aboutHeroSubtitle", "Learn more about our company")}
          />
          <div className="animate-pulse">
            <div className="mb-4 h-12 w-72 rounded bg-white/20" />
            <div className="h-6 w-48 rounded bg-white/10" />
          </div>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <PageContainer>
        <PageHero
          title={at("aboutHeroTitle", "About Us")}
          description={at("aboutHeroSubtitle", "Learn more about our company")}
          chips={[
            { label: at("ceoRole", "Founder"), href: "#about-founder" },
            { label: at("visionTitle", "Vision"), href: "#about-principles" },
            { label: at("advantagesTitle", "Why Dev48"), href: "#about-why" },
          ]}
          actions={[
            {
              href: getPathWithLocale("/book-call?source=about", locale),
              label: isRu ? "Обсудить проект" : "Discuss a project",
              primary: true,
              ctaLocation: "pagehero_about_primary",
            },
            {
              href: CONTACTS.telegramUrl,
              label: isRu ? "Связаться в Telegram" : "Message on Telegram",
              ctaLocation: "pagehero_about_secondary",
            },
          ]}
        />
      </PageContainer>

      {/* Основатель */}
      <PageSection id="about-founder">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Image
                src="/images/ceo_dev48.webp"
                alt={at("ceoName", "Henadzi Bondarau")}
                width={714}
                height={952}
                sizes="(max-width: 640px) 100vw, 384px"
                className="w-full max-w-sm rounded-2xl"
              />
            </motion.div>
          </div>
          <div className="flex flex-col justify-center md:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs uppercase tracking-wider text-white/40">{at("ceoRole", "Founder")}</p>
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-white/70">
                <p>{at("ceoPara1", "")}</p>
                <p>{at("ceoPara2", "")}</p>
                <p>{at("ceoPara3", "")}</p>
                <p>{at("ceoPara4", "")}</p>
                <p>{at("ceoPara5", "")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </PageSection>

      {/* Видение, миссия, ценности */}
      <PageSection id="about-principles" tone="muted">
        <div className="grid gap-4 md:grid-cols-2">
          <GlassCard>
            <h2 className="text-xs uppercase tracking-wider text-white/40">{at("visionTitle", "Vision")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-white/70">{at("visionDesc", "Our vision")}</p>
          </GlassCard>
          <GlassCard>
            <h2 className="text-xs uppercase tracking-wider text-white/40">{at("missionTitle", "Mission")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-white/70">{at("missionDesc", "Our mission")}</p>
          </GlassCard>
        </div>
        <div className="mt-8">
          <h2 className="mb-4 text-xs uppercase tracking-wider text-white/40">{at("valuesTitle", "Values")}</h2>
          <ValuesCarousel />
        </div>
      </PageSection>

      {/* Преимущества */}
      <PageSection id="about-why">
        <SectionTitle
          title={at("advantagesTitle", "Why Dev48")}
          description={
            isRu
              ? "Четыре принципа, по которым мы работаем с клиентами."
              : "Four principles we follow with every client."
          }
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {ADVANTAGES.map((adv, index) => (
            <motion.div
              key={adv.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <GlassCard className="h-full">
                <h3 className="text-base font-semibold text-white">{at(adv.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{at(adv.desc)}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </PageSection>

      <PageCta
        title={isRu ? "Обсудим ваш проект?" : "Shall we discuss your project?"}
        description={
          isRu
            ? "Расскажите о задаче — вернёмся с планом, сроками и составом команды."
            : "Tell us about the task — we will come back with a plan, timeline and team."
        }
        actions={[
          {
            href: getPathWithLocale("/book-call?source=about", locale),
            label: isRu ? "Обсудить проект" : "Discuss a project",
            primary: true,
            ctaLocation: "pagecta_about_primary",
          },
          {
            href: getPathWithLocale("/services", locale),
            label: isRu ? "Смотреть услуги" : "See services",
            ctaLocation: "pagecta_about_secondary",
          },
        ]}
      />
    </div>
  );
}
